import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

/**
 * Revue des propositions de catégorisation IA avant écriture (LAP).
 *
 * Affiche chaque opération avec la sous-catégorie proposée (éditable), le score
 * de confiance, et permet la correction unitaire OU en masse. Les faibles
 * confiances sont surlignées et remontées en tête. Tout est coché par défaut ;
 * l'utilisateur décoche ce qu'il ne veut pas appliquer.
 *
 * Props :
 *  - proposals       : [{op_id, libelle, montant, date, account, current,
 *                        souscategorie, keyword, confidence}]
 *  - subcategories   : [{ name }] options du Select
 *  - notProposedCount: nb d'opérations que l'IA n'a pas su proposer
 *  - applying        : bool — application en cours
 *  - applyProgress   : { processed, total } | null
 *  - onApply(assignments) : lance l'application des lignes cochées
 *  - onBack()        : revenir au paramétrage
 */
const LOW_CONFIDENCE = 0.6
const HIGH_CONFIDENCE = 0.8

function confidenceColor(c) {
  if (c == null) return 'default'
  if (c < LOW_CONFIDENCE) return 'warning'
  if (c >= HIGH_CONFIDENCE) return 'success'
  return 'default'
}

function fmtMoney(v) {
  const n = Number(v || 0)
  return n.toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export default function CategorizationReview({
  proposals,
  subcategories,
  notProposedCount = 0,
  applying = false,
  applyProgress = null,
  onApply,
  onBack,
}) {
  const { t } = useTranslation('layout')
  const tr = (k, dflt, opts) =>
    t(`finance.categorisation.review.${k}`, { defaultValue: dflt, ...(opts || {}) })

  // Lignes éditables, triées : faible confiance d'abord (null = plus faible).
  const initialRows = React.useMemo(() => {
    const rows = (proposals || []).map((p) => ({
      ...p,
      include: true,
      souscategorie: p.souscategorie,
    }))
    rows.sort((a, b) => {
      const ca = a.confidence == null ? -1 : a.confidence
      const cb = b.confidence == null ? -1 : b.confidence
      return ca - cb
    })
    return rows
  }, [proposals])

  const [rows, setRows] = React.useState(initialRows)
  const [selected, setSelected] = React.useState(() => new Set())
  const [bulkValue, setBulkValue] = React.useState('')
  const [filter, setFilter] = React.useState('')

  React.useEffect(() => {
    setRows(initialRows)
    setSelected(new Set())
  }, [initialRows])

  // Options filtrées selon le sens de l'opération : montant < 0 => Dépense,
  // sinon Revenu. Un même nom peut exister dans les deux mouvements.
  const optionsFor = React.useCallback(
    (montant) => {
      const wanted = Number(montant) < 0 ? 'Dépense' : 'Revenu'
      const seen = new Set()
      const list = []
      ;(subcategories || []).forEach((s) => {
        if (!s || !s.name) return
        if (s.mouvement && s.mouvement !== wanted) return
        if (seen.has(s.name)) return
        seen.add(s.name)
        list.push({ name: s.name, categorie: s.categorie || '' })
      })
      list.sort((a, b) => a.name.localeCompare(b.name))
      return list
    },
    [subcategories],
  )

  // Options = sous-catégories (nom + catégorie parente pour lever l'ambiguïté).
  const options = React.useMemo(() => {
    const seen = new Set()
    const list = []
    ;(subcategories || []).forEach((s) => {
      const name = s && s.name
      if (name && !seen.has(name)) {
        seen.add(name)
        list.push({ name, categorie: s.categorie || '' })
      }
    })
    list.sort((a, b) => a.name.localeCompare(b.name))
    return list
  }, [subcategories])

  const visibleRows = React.useMemo(() => {
    if (!filter.trim()) return rows
    const f = filter.trim().toLowerCase()
    return rows.filter(
      (r) =>
        (r.libelle || '').toLowerCase().includes(f) ||
        (r.souscategorie || '').toLowerCase().includes(f) ||
        (r.account || '').toLowerCase().includes(f),
    )
  }, [rows, filter])

  const includedCount = rows.filter((r) => r.include).length

  const setRow = (opId, patch) =>
    setRows((prev) => prev.map((r) => (r.op_id === opId ? { ...r, ...patch } : r)))

  const toggleAll = (checked) =>
    setRows((prev) => prev.map((r) => ({ ...r, include: checked })))

  const toggleSelect = (opId) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(opId)) next.delete(opId)
      else next.add(opId)
      return next
    })

  const selectAllVisible = (checked) =>
    setSelected(() => {
      if (!checked) return new Set()
      return new Set(visibleRows.map((r) => r.op_id))
    })

  const applyBulkCategory = () => {
    if (!bulkValue || selected.size === 0) return
    setRows((prev) =>
      prev.map((r) =>
        selected.has(r.op_id) ? { ...r, souscategorie: bulkValue, include: true } : r,
      ),
    )
  }

  const bulkInclude = (checked) =>
    setRows((prev) =>
      prev.map((r) => (selected.has(r.op_id) ? { ...r, include: checked } : r)),
    )

  const handleApply = () => {
    const assignments = rows
      .filter((r) => r.include && r.souscategorie)
      .map((r) => ({ op_id: r.op_id, souscategorie: r.souscategorie, keyword: r.keyword || '' }))
    onApply(assignments)
  }

  if (applying) {
    const pct =
      applyProgress && applyProgress.total > 0
        ? Math.min(100, Math.round((applyProgress.processed / applyProgress.total) * 100))
        : undefined
    return (
      <Stack spacing={2} sx={{ py: 3 }}>
        <Typography variant="body2">
          {tr('applying', 'Application en cours… {{processed}} / {{total}}', {
            processed: applyProgress ? applyProgress.processed : 0,
            total: applyProgress ? applyProgress.total : includedCount,
          })}
        </Typography>
        <LinearProgress variant={pct != null ? 'determinate' : 'indeterminate'} value={pct} />
      </Stack>
    )
  }

  return (
    <Stack spacing={1.5}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {tr('title', 'Vérifiez les propositions')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {tr(
            'subtitle',
            'Corrigez une catégorie ou plusieurs en masse. Les faibles confiances sont surlignées et placées en haut.',
          )}
        </Typography>
      </Box>

      {notProposedCount > 0 && (
        <Alert severity="info" sx={{ py: 0 }}>
          {tr('notProposed', "{{count}} opération(s) n'ont pas pu être proposées par l'IA et resteront non catégorisées.", {
            count: notProposedCount,
          })}
        </Alert>
      )}

      {/* Barre d'actions en masse */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ bgcolor: 'action.hover', p: 1, borderRadius: 1 }}
      >
        <Typography variant="body2" sx={{ flexShrink: 0 }}>
          {tr('selectedCount', '{{count}} sélectionnée(s)', { count: selected.size })}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={selected.size === 0}>
          <InputLabel>{tr('bulkAssign', 'Affecter à la sélection')}</InputLabel>
          <Select
            label={tr('bulkAssign', 'Affecter à la sélection')}
            value={bulkValue}
            onChange={(e) => setBulkValue(e.target.value)}
          >
            {options.map((o) => (
              <MenuItem key={o.name} value={o.name}>
                {o.name}
                {o.categorie ? (
                  <Typography component="span" sx={{ color: 'text.secondary', fontSize: 11, ml: 1 }}>
                    {o.categorie}
                  </Typography>
                ) : null}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          disabled={!bulkValue || selected.size === 0}
          onClick={applyBulkCategory}
        >
          {tr('applyToSelection', 'Appliquer à la sélection')}
        </Button>
        <Button size="small" disabled={selected.size === 0} onClick={() => bulkInclude(true)}>
          {tr('checkSelection', 'Cocher')}
        </Button>
        <Button size="small" disabled={selected.size === 0} onClick={() => bulkInclude(false)}>
          {tr('uncheckSelection', 'Décocher')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder={tr('search', 'Filtrer…')}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ minWidth: 160 }}
        />
      </Stack>

      <TableContainer sx={{ maxHeight: 420, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={visibleRows.length > 0 && visibleRows.every((r) => selected.has(r.op_id))}
                  indeterminate={
                    visibleRows.some((r) => selected.has(r.op_id)) &&
                    !visibleRows.every((r) => selected.has(r.op_id))
                  }
                  onChange={(e) => selectAllVisible(e.target.checked)}
                />
              </TableCell>
              <TableCell padding="checkbox">
                <Tooltip title={tr('includeAll', 'Tout cocher / décocher')}>
                  <Checkbox
                    size="small"
                    checked={rows.length > 0 && rows.every((r) => r.include)}
                    indeterminate={rows.some((r) => r.include) && !rows.every((r) => r.include)}
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                </Tooltip>
              </TableCell>
              <TableCell>{tr('colDate', 'Date')}</TableCell>
              <TableCell>{tr('colLabel', 'Libellé')}</TableCell>
              <TableCell align="right">{tr('colAmount', 'Montant')}</TableCell>
              <TableCell sx={{ minWidth: 200 }}>{tr('colCategory', 'Sous-catégorie')}</TableCell>
              <TableCell align="center">{tr('colConfidence', 'Confiance')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((r) => {
              const low = r.confidence != null && r.confidence < LOW_CONFIDENCE
              const rowOptions = optionsFor(r.montant)
              return (
                <TableRow
                  key={r.op_id}
                  hover
                  sx={low ? { bgcolor: 'rgba(251,191,36,0.12)' } : undefined}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selected.has(r.op_id)}
                      onChange={() => toggleSelect(r.op_id)}
                    />
                  </TableCell>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={r.include}
                      onChange={(e) => setRow(r.op_id, { include: e.target.checked })}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{r.date || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography variant="body2" noWrap title={r.libelle}>
                      {r.libelle}
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {r.source && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={r.source}
                          color={r.source === 'ia' ? 'primary' : r.source === 'règle' ? 'success' : 'default'}
                          sx={{ height: 16, fontSize: 9.5, '& .MuiChip-label': { px: 0.6 } }}
                        />
                      )}
                      {r.new_category && (
                        <Chip size="small" color="warning" label={tr('newCategory', 'nouvelle')} sx={{ height: 16, fontSize: 9.5, '& .MuiChip-label': { px: 0.6 } }} />
                      )}
                      {r.account && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {r.account}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                      color: Number(r.montant) < 0 ? 'error.main' : 'success.main',
                    }}
                  >
                    {fmtMoney(r.montant)}
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      fullWidth
                      value={
                        rowOptions.some((o) => o.name === r.souscategorie) || r.new_category
                          ? r.souscategorie
                          : ''
                      }
                      onChange={(e) => setRow(r.op_id, { souscategorie: e.target.value, include: true })}
                    >
                      {r.new_category && !rowOptions.some((o) => o.name === r.souscategorie) && (
                        <MenuItem value={r.souscategorie}>
                          {r.souscategorie}
                          <Typography component="span" sx={{ color: 'warning.main', fontSize: 11, ml: 1 }}>
                            {tr('newCategory', 'nouvelle')}
                          </Typography>
                        </MenuItem>
                      )}
                      {rowOptions.map((o) => (
                        <MenuItem key={o.name} value={o.name}>
                          {o.name}
                          {o.categorie ? (
                            <Typography component="span" sx={{ color: 'text.secondary', fontSize: 11, ml: 1 }}>
                              {o.categorie}
                            </Typography>
                          ) : null}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      color={confidenceColor(r.confidence)}
                      variant={r.confidence == null ? 'outlined' : 'filled'}
                      label={r.confidence == null ? '—' : `${Math.round(r.confidence * 100)}%`}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  {tr('noRows', 'Aucune proposition à afficher.')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button onClick={onBack} disabled={applying}>
          {tr('back', 'Retour')}
        </Button>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {tr('willApply', '{{count}} affectation(s) seront appliquées', { count: includedCount })}
          </Typography>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={applying || includedCount === 0}
            startIcon={applying ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {tr('apply', 'Appliquer')}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  )
}
